import { useState, useReducer } from "react";
import { navigate } from "@reach/router";
import { ButtonTW } from "../../../comp/tw/Button";
import Build from "./Build/build";
import ChainlinkDesigns from "./ChainlinkDesigns";
import { v4 } from "uuid";

const defaultDesign = {
  name: "",
  image: "",
  postSpacing: "",
  topRailLength: "",
  concretePerLinePost: "",
  concretePerMainPost: "",
  tensionBandsPerMainPost: "",
  maxLengthBetweenMainPosts: "",
  fenceTiesEveryFt: "",
  tensionWireLength: "",
  meshLength: "",
  materials: {
    mesh: {
      category: "mesh",
      name: "",
      qty: "",
      price: "",
      rollLength: "",
      rollCost: "",
      included: false,
    },
    mainPost: {
      category: "mainPost",
      name: "",
      price: "",
      included: false,
    },
    mainPostCap: {
      category: "mainPostCap",
      name: "",
      price: "",
      included: false,
    },
    linePost: {
      category: "linePost",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    linePostCap: {
      category: "linePostCap",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    topRail: {
      category: "topRail",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    topRailEndConnector: {
      category: "topRailEndConnector",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    topRailSleeve: {
      category: "topRailSleeve",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    centerMainPostBand: {
      category: "centerMainPostBand",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    nutAndBolt: {
      category: "nutAndBolt",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    tensionBand: {
      category: "tensionBand",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    tensionBar: {
      category: "tensionBar",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    concrete: {
      category: "concrete",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    fenceTies: {
      category: "fenceTies",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    tensionWire: {
      category: "tensionWire",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    barbedWire: {
      category: "barbedWire",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
  },
  gate: {
    gate: {
      category: "gate",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    singleGateKit: {
      category: "singleGateKit",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    caneBolt: {
      category: "caneBolt",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    doubleGateKit: {
      category: "doubleGateKit",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
    gateExtension: {
      category: "gateExtension",
      name: "",
      qty: "",
      price: "",
      included: false,
    },
  },
};

function reducer(state, action) {
  if (action.type === "updateImage") {
    return {
      ...state,
      image: action.payload,
    };
  }

  if (action.type === "updateDesignName") {
    return {
      ...state,
      name: action.payload,
    };
  }

  if (action.type === "updateField") {
    return {
      ...state,
      [action.payload.field]: action.payload.value,
    };
  }

  if (action.type === "updateMaterial") {
    return {
      ...state,
      materials: {
        ...state.materials,
        [action.payload.key]: {
          ...state.materials[action.payload.key],
          [action.payload.field]: action.payload.value,
        },
      },
    };
  }

  if (action.type === "updateGate") {
    return {
      ...state,
      gate: {
        ...state.gate,
        [action.payload.key]: {
          ...state.gate[action.payload.key],
          [action.payload.field]: action.payload.value,
        },
      },
    };
  }

  if (action.type === "toggleMaterial") {
    return {
      ...state,
      materials: {
        ...state.materials,
        [action.payload.key]: {
          ...state.materials[action.payload.key],
          included: !state.materials[action.payload.key].included,
        },
      },
    };
  }

  if (action.type === "toggleGate") {
    return {
      ...state,
      gate: {
        ...state.gate,
        [action.payload.key]: {
          ...state.gate[action.payload.key],
          included: !state.gate[action.payload.key].included,
        },
      },
    };
  }

  if (action.type === "copyDesign") {
    return {
      ...action.payload,
      id: v4(),
    };
  }

  if (action.type === "resetDesign") {
    return defaultDesign;
  }

  throw Error("Unknown action.");
}

const ChainlinkDesign = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [designPageState, dispatch] = useReducer(reducer, defaultDesign);

  const handleTabClick = (tabNumber) => {
    setActiveTab(tabNumber);
  };

  return (
    <div className="w-full max-w mx-auto">
      {/* Fence Type Header with Back Button */}
      <div className="mb-4 flex items-center justify-between p-4  rounded-lg">
        <div className="flex items-center gap-4">
          <ButtonTW onClick={() => navigate("/design")} label="← Back" />
          <h2 className="text-2xl font-semibold">Chain Link Designs</h2>
        </div>
      </div>

      {/* Tab Headers */}
      <div className="text-lg font-semibold flex border-b border-gray-300">
        <button
          className={`py-2 px-4 w-1/3 ${
            activeTab === 1
              ? "text-indigo-500 border-b-2 border-indigo-500"
              : "text-gray-500"
          }`}
          onClick={() => handleTabClick(1)}
        >
          Designs
        </button>
        <button
          className={`py-2 px-4 w-1/3 ${
            activeTab === 2
              ? "text-indigo-500 border-b-2 border-indigo-500"
              : "text-gray-500"
          }`}
          onClick={() => handleTabClick(2)}
        >
          Build
        </button>
        <button
          className={`py-2 px-4 w-1/3 ${
            activeTab === 3
              ? "text-indigo-500 border-b-2 border-indigo-500"
              : "text-gray-500"
          }`}
          onClick={() => handleTabClick(3)}
        >
          Materials
        </button>
      </div>

      {/* Tab Contents */}
      <div className="p-4">
        {activeTab === 1 && (
          <ChainlinkDesigns dispatch={dispatch} setActiveTab={setActiveTab} />
        )}
        {activeTab === 2 && (
          <Build designPageState={designPageState} dispatch={dispatch} />
        )}
        {activeTab === 3 && (
          <div>
            <h3 className="text-xl font-semibold mb-4">Chain Link Materials</h3>
            <p>
              Manage chain link materials, fabric, posts, top rails, and
              hardware.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChainlinkDesign;
